import React, { useContext, useMemo } from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "../ui/tooltip";
import { Home, Trash2, Plus, Check, ChevronDown, ChevronRight, Info, Ruler } from "lucide-react";
import { fmtEUR, UNIT_LABEL } from "../../lib/calculator";
import { EditableDescription } from "./EditableDescription";
import { autoSelectOnFocus } from "../../lib/autoNameSelect";

// v6 F42k Correctieronde v8 — Labels voor exclusieve choice-groups in de UI.
// Getoond als kleine header boven de eerste regel van een groep, zodat de
// gebruiker meteen ziet dat slechts één alternatief tegelijk gekozen kan
// worden (backend legt geen exclusiviteit op; frontend regelt dat via
// snapshot + uncheck-andere in toggleRoomAction).
const CG_HEADERS = {
  schuurgraad: "SCHUURGRAAD — KIES ÉÉN",
  spuit_schuren: "SCHUURGRAAD — KIES ÉÉN",
  plamuurwerk: "PLAMUURWERK — KIES ÉÉN",
  plaatselijk_herstel: "PLAATSELIJK HERSTEL — KIES ÉÉN",
  wandbekleding: "WANDBEKLEDING — KIES ÉÉN",
  dakoversteek_finish: "EINDAFWERKING — KIES ÉÉN",
};
import {
  ROOM_TYPES, ROOM_DEFAULTS, autoQuantity, autoQuantityForAction, roomWallDeductionTotal,
  wallsGrossFromSegments, ceilingGrossFromSegments,
  buildDefaultSegmentsFromQuick,
} from "../../lib/roomDefaults";
import { groupRoomActions } from "../../lib/roomSections";
import { AutoSourcesContext } from "../../lib/autoSource";
import NumF from "./NumF";
import PieceStepper from "./PieceStepper";
import RawNumInput from "./RawNumInput";
import MeasurementRow from "./MeasurementRow";
import RoomDeductionsBlock from "./RoomDeductionsBlock";
import StairPropsBlock from "./StairPropsBlock";

export default function RoomQuickFlow({
  room, measurements, extras, summary, priceCodes, codeByCode, projectVat,
  onUpdateRoom, onRemove, onToggleAction, onAddCustomMeasurement,
  onUpdateM, onRemoveM, onSwitchAdvanced,
  // v6 E1 — when a template is active on the project, defaults come from the
  // template's lines instead of ROOM_DEFAULTS (hardcoded standard painting).
  // Falls back to ROOM_DEFAULTS when null.
  activeTemplateActions = null,
  // v6 E8 — per-room template override.
  // `roomTemplateOptions`: array of full Template objects suitable for rooms.
  // `onChangeRoomTemplate(templateId | "")`: called when user picks a template.
  //   Parent handles the confirm-dialog when room has existing measurements.
  roomTemplateOptions = [],
  onChangeRoomTemplate = null,
  // v6 F2 — accordion: when `collapsed=true`, render only the summary header
  // (kamernaam, template-badge, netto m², #regels, subtotaal). State + persist
  // is owned by the parent (PaintMeasure.jsx).
  collapsed = false,
  onToggleCollapsed = null,
  // Full template list for resolving the active room template's name on the
  // collapsed summary badge.
  templates = [],
}) {
  // v6 F16 S4 — Auto-source catalog uit context (voor "Auto: <label>" hints)
  const autoSources = useContext(AutoSourcesContext) || {};
  // v6 F42k Correctieronde v3 — Legacy behoud: als de room een template_id
  // heeft die niet meer in de nieuwe filter zit (bv. oude offerte met
  // "Meerwerken standaard" of "Trapwerken" per ongeluk gekoppeld), toon
  // dat sjabloon toch in de dropdown met een "(legacy)"-suffix zodat de
  // user zijn keuze kan behouden of vervangen. Nieuwe keuzes komen
  // uitsluitend uit `roomTemplateOptions`.
  const displayedTemplateOptions = useMemo(() => {
    if (!room.template_id) return roomTemplateOptions;
    const alreadyIn = roomTemplateOptions.some((t) => t.id === room.template_id);
    if (alreadyIn) return roomTemplateOptions;
    const legacyTpl = (templates || []).find((t) => t.id === room.template_id);
    if (!legacyTpl) return roomTemplateOptions;
    return [...roomTemplateOptions, { ...legacyTpl, __legacyPassthrough: true }];
  }, [roomTemplateOptions, templates, room.template_id]);
  const defaults = activeTemplateActions || ROOM_DEFAULTS[room.room_type] || ROOM_DEFAULTS.other;
  const isStair = room.room_type === "stair";
  const isGeneral = room.room_type === "general";
  // v6 F9.3 — Zones zonder geometrie (trap + algemeen) verbergen L/B/H, meet-
  // methode en wand-aftrekken. 'isSpecialZone' bundelt die check.
  const isSpecialZone = isStair || isGeneral;
  const L = +room.length || 0;
  const W = +room.width || 0;
  const H = +room.height || 0;
  // v6 F7 — meetmethode-aware berekening van bruto muren en plafond.
  // v6 F9.2/F9.3 — Trap- of Algemeen-zones hebben geen L/B/H; bypass alles.
  const isFree = !isSpecialZone && room?.measure_method === "free";
  const wallGross = isSpecialZone
    ? 0
    : (isFree ? wallsGrossFromSegments(room) : Math.max(0, 2 * (L + W) * H));
  const wallDedu = isSpecialZone ? 0 : roomWallDeductionTotal(room);
  const wallArea = Math.max(0, wallGross - wallDedu);
  const ceilingArea = isSpecialZone
    ? 0
    : (isFree ? ceilingGrossFromSegments(room) : Math.max(0, L * W));

  // A measurement matches an action when EITHER its price_code_id corresponds
  // to the action's code, OR (fallback when priceCodes wasn't yet loaded at
  // creation time) it has the same description as the action label. This means
  // measurements added via Detail-modus or "Andere code toevoegen" fold back
  // into the checklist UX whenever their description matches a default action.
  const isMatch = (m, action) => {
    const pcId = codeByCode[action.code]?.id;
    if (pcId && m.price_code_id === pcId) return true;
    if (m.description && m.description === action.label) return true;
    return false;
  };
  const actionMatch = (action) => measurements.find((m) => isMatch(m, action));
  // "Andere regels" only contains measurements that match NO default action.
  const customMs = measurements.filter((m) => !defaults.some((a) => isMatch(m, a)));

  // v6 F2 — Collapsed summary data
  const totalLines = measurements.length + (extras?.length || 0);
  // v6 F11.7 — Template-mapping bug fix: enkel de DB-bron als waarheid.
  // Geen hardcoded fallback "Standaard" of "Project-template" badge.
  // Wanneer room.template_id gezet is maar de template niet meer bestaat,
  // tonen we expliciet "Template niet gevonden" zodat de user actie ondernemen.
  const activeRoomTpl = room.template_id ? templates.find((t) => t.id === room.template_id) : null;
  // v6 F16.2 — Onderscheid tussen "verouderd (active=false)" en "echt weg".
  // Verouderd → rustige amber badge, "echt weg" → rode error zoals voorheen.
  const roomTemplateLegacy = !!room.template_id && !!activeRoomTpl && activeRoomTpl.active === false;
  const roomTemplateMissing = !!room.template_id && !activeRoomTpl;
  const tplBadgeLabel = roomTemplateLegacy
    ? "Verouderd sjabloon"
    : roomTemplateMissing
      ? "Template niet gevonden"
      : (activeRoomTpl?.name || (activeTemplateActions ? "Project-sjabloon" : "Geen sjabloon"));
  const tplBadgeIsOverride = !!room.template_id && !roomTemplateMissing && !roomTemplateLegacy;

  // v6 F1 — render a single action row (used by every section bucket).
  const renderActionRow = (a) => {
    const pc = codeByCode[a.code];
    const match = actionMatch(a);
    const checked = !!match;
    const unitLabel = pc ? UNIT_LABEL[pc.unit] : "-";
    const defaultPrice = pc ? +pc.default_price : 0;
    // v6 F42k Correctieronde v6 — Uniforme prijs-bewerking (identiek patroon
    // aan JoineryBlock / ZoneTemplateBlock). unit_price_override op de
    // measurement bepaalt de effectieve prijs voor regel- en zone-totaal.
    const rowOverride = match && match.unit_price_override != null && match.unit_price_override !== ""
      ? +match.unit_price_override : null;
    const unitPrice = rowOverride != null ? rowOverride : defaultPrice;
    // v6 E3 — per price-code wall-deduction opt-in. Only meaningful when this
    // row is a wall-based action (auto_kind=walls).
    const applyDed = !!pc?.apply_wall_deductions;
    const isWalls = a.auto_kind === "walls";
    let qty = 0;
    if (match) {
      // v6 F10.2 — Ceiling/floor display fix:
      //
      // De measurement-quantity voor plafond/vloer wordt op TWEE manieren
      // bijgehouden, afhankelijk van de meetmethode:
      //   • Snelle opmeting (quick) → match.length × match.width
      //     (set door buildAutoMeasurement + updateRoomDimensions:457)
      //   • Vrije opmeting (free)   → match.quantity
      //     (set door updateRoomDimensions:455 = som van ceiling_segments;
      //      L/W blijven 0 omdat de bounding-box niet representatief is)
      //
      // De vroegere display-logica gebruikte ALTIJD L×W → toonde 0,00 m² voor
      // free-mode kamers én voor legacy measurements waar L=W=0 maar quantity
      // wel correct gevuld was (bug-rapport: na aanvinken sprong de regel van
      // 8,13 m² preview naar 0,00 m²).
      //
      // Nieuwe logica:
      //   1. Free-mode ceiling → lees match.quantity
      //   2. Anders L×W zoals voorheen
      //   3. Fallback: als L×W=0 maar match.quantity>0 → gebruik match.quantity
      //      (legacy/orphan re-sync gevallen)
      if (a.auto_kind === "ceiling" || a.auto_kind === "floor") {
        const isFreeRoom = room?.measure_method === "free";
        // v6 F10.4 — Free-mode ceiling én floor lezen quantity (segments-sum).
        // Quick-mode blijft L×W met legacy-fallback voor stale data.
        if (isFreeRoom) {
          qty = +match.quantity || 0;
          if (qty === 0 && +match.length && +match.width) {
            qty = +match.length * +match.width;
          }
        } else {
          qty = (+match.length || 0) * (+match.width || 0);
          if (qty === 0 && +match.quantity > 0) qty = +match.quantity;
        }
      }
      else if (a.auto_kind === "plinten" || a.auto_kind === "linear") qty = +match.length || 0;
      else if (a.auto_kind === "fixed") qty = +match.quantity || 1;
      else qty = +match.quantity || 0;
    } else {
      qty = autoQuantityForAction(a, room, applyDed);
    }
    const total = qty * unitPrice;
    const isPiece = a.auto_kind === "piece";
    // v6 F9.1 — Eenheden waar de gebruiker de hoeveelheid manueel ingeeft
    // (geen auto-fill uit kamerafmetingen): lopende meters, uren, losse m²
    // van objectcodes (T-03/T-04 etc.) en forfait (default 1 maar
    // aanpasbaar). Toon een decimale getalveld i.p.v. een read-only label.
    const isManualQty = ["linear", "hour", "manual", "fixed"].includes(a.auto_kind);
    const helperNote = pc?.notes && pc.notes !== "Default professional painting price, editable per project"
      ? pc.notes : "";
    return (
      <div key={a.code}
        className={`flex items-center gap-3 px-3 py-2.5 ${checked ? "bg-[#FFF8F2]" : "hover:bg-[#FAF9F6]"}`}
        data-testid={`action-row-${room.id}-${a.code}`}
      >
        <button
          onClick={() => onToggleAction(a)}
          className={`w-7 h-7 rounded-lg border-2 flex items-center justify-center flex-shrink-0 transition ${checked ? "bg-[#D96C4A] border-[#D96C4A] text-white" : "border-[#C9C8C2] hover:border-[#D96C4A]"}`}
          data-testid={`action-check-${room.id}-${a.code}`}
          aria-label={checked ? "Werk verwijderen" : "Werk toevoegen"}
        >
          {checked && <Check className="w-4 h-4" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-[#2C2C2A]">
            {checked && match ? (
              <div className="flex items-start gap-1.5">
                <EditableDescription
                  value={match.description_snapshot || pc?.description || a.label}
                  override={match.description_override}
                  defaultValue={match.description_snapshot || pc?.description || a.label}
                  onOverride={(text) => onUpdateM(match.id, { description_override: text })}
                  onReset={() => onUpdateM(match.id, { description_override: null })}
                  testId={`action-desc-${room.id}-${a.code}`}
                  multiline
                  className="flex-1"
                />
                {helperNote && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className="text-[#5D7259] hover:text-[#D96C4A] flex-shrink-0 mt-1"
                        data-testid={`action-info-${room.id}-${a.code}`}
                        aria-label="Toelichting"
                      >
                        <Info className="w-3.5 h-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[240px] text-xs">{helperNote}</TooltipContent>
                  </Tooltip>
                )}
              </div>
            ) : (
              <div className="truncate flex items-center gap-1.5">
                {a.label}
                {helperNote && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className="text-[#5D7259] hover:text-[#D96C4A] flex-shrink-0"
                        data-testid={`action-info-${room.id}-${a.code}`}
                        aria-label="Toelichting"
                      >
                        <Info className="w-3.5 h-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[240px] text-xs">{helperNote}</TooltipContent>
                  </Tooltip>
                )}
              </div>
            )}
          </div>
          <div className="text-[10px] text-[#5D7259] font-mono">{a.code} · {pc?.work_category || "—"}</div>
          {/* v6 F16 S4 — Auto-source hint. Toont "Auto: <NL-label>" wanneer de
              matched measurement een auto_source gekoppeld heeft (bv. via een
              schrijnwerk-sjabloonregel die naar 'joinery.door_blade' wijst). */}
          {match?.auto_source && autoSources[match.auto_source] && (
            <div
              className="text-[10px] text-[#3F5E3F] font-semibold truncate mt-0.5"
              data-testid={`action-auto-hint-${room.id}-${a.code}`}
            >
              Auto: {autoSources[match.auto_source].label}
            </div>
          )}
          {/* v6 F36 — Mobiele prijs-samenvatting per regel. Toont
              berekening (qty × prijs) links; totaal staat rechts naast qty. */}
          <div
            className="sm:hidden text-[11px] font-mono text-[#2C2C2A] mt-1"
            data-testid={`action-price-summary-mobile-${room.id}-${a.code}`}
          >
            {a.auto_kind === "fixed"
              ? `1 forfait × ${fmtEUR(unitPrice)}`
              : `${qty.toLocaleString("nl-BE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${unitLabel} × ${fmtEUR(unitPrice)}/${unitLabel}`}
          </div>
        </div>
        <div className="text-[11px] text-[#4F4F4D] hidden sm:block w-12 text-right">{unitLabel}</div>
        {/* v6 F42k Correctieronde v6 — Uniforme prijs-editor. Identiek patroon
            aan JoineryBlock/ZoneTemplateBlock: RawNumInput → measurement.unit_price_override.
            Alleen zichtbaar wanneer regel checked is; anders read-only label. */}
        <div className="hidden sm:flex flex-wrap items-center gap-x-1 gap-y-0.5 w-24 justify-end">
          {checked && match ? (
            <>
              <span className="text-[11px] text-[#4F4F4D]">€</span>
              <RawNumInput
                value={rowOverride != null ? rowOverride : ""}
                allowEmpty
                onChange={(v) => {
                  if (v === "" || v === null || v === undefined) {
                    onUpdateM(match.id, { unit_price_override: null });
                  } else {
                    onUpdateM(match.id, { unit_price_override: +v });
                  }
                }}
                placeholder={defaultPrice.toFixed(2)}
                className="w-16 h-8 rounded-md text-right text-sm"
                data-testid={`action-price-${room.id}-${a.code}`}
              />
              {rowOverride != null && (
                <span
                  className="text-[9px] uppercase tracking-wider font-bold text-[#B07A4F]"
                  data-testid={`action-price-override-badge-${room.id}-${a.code}`}
                  title="Aangepast voor dit project — centrale prijslijst blijft ongewijzigd"
                >
                  Aangepast
                </span>
              )}
            </>
          ) : (
            <span className="text-[11px] text-[#4F4F4D]">€ {defaultPrice.toFixed(2)}/{unitLabel}</span>
          )}
        </div>
        {isPiece ? (
          <PieceStepper
            value={match ? +match.quantity || 0 : 0}
            disabled={!checked}
            onChange={(v) => match && onUpdateM(match.id, { quantity: v })}
            testId={`action-qty-${room.id}-${a.code}`}
          />
        ) : isManualQty ? (
          // v6 F9.1 — decimale invoer voor lm (length), uur/forfait/object-m² (quantity)
          (() => {
            const useLength = a.auto_kind === "linear";
            const curVal = match
              ? (useLength ? (+match.length || 0) : (+match.quantity || (a.auto_kind === "fixed" ? 1 : 0)))
              : 0;
            const writePatch = (v) => useLength ? { length: v } : { quantity: v };
            return (
              <div className="flex items-center gap-1">
                <RawNumInput
                  value={curVal}
                  disabled={!checked}
                  onChange={(v) => match && onUpdateM(match.id, writePatch(v))}
                  className="w-20 h-8 rounded-md text-right font-mono text-sm"
                  placeholder="0"
                  data-testid={`action-qty-${room.id}-${a.code}`}
                />
                <span className="text-[10px] text-[#4F4F4D] w-8">{unitLabel}</span>
              </div>
            );
          })()
        ) : (
          <div className="w-24 text-right">
            <div className="text-sm font-bold text-[#2C2C2A]" data-testid={`action-qty-${room.id}-${a.code}`}>
              {qty.toLocaleString("nl-BE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {unitLabel}
            </div>
            {isWalls && (
              <div
                className={`hidden sm:block text-[9px] uppercase tracking-wider font-semibold ${applyDed ? "text-[#6B8E6B]" : "text-[#5D7259]"}`}
                data-testid={`action-area-mode-${room.id}-${a.code}`}
              >
                {applyDed ? "netto" : "bruto"}
              </div>
            )}
            {/* v6 F36.1 — Totaalprijs onder qty op mobile (sm:hidden) zodat
                de gebruiker regelbedrag direct naast de hoeveelheid ziet.
                v14e final — mobile prijs-editor ook hier: user kan prijs
                aanpassen zonder desktop nodig te hebben. */}
            {checked && match ? (
              <div className="sm:hidden mt-0.5 flex items-center justify-end gap-1">
                <span className="text-[10px] text-[#4F4F4D]">€</span>
                <RawNumInput
                  value={rowOverride != null ? rowOverride : ""}
                  allowEmpty
                  onChange={(v) => {
                    if (v === "" || v === null || v === undefined) {
                      onUpdateM(match.id, { unit_price_override: null });
                    } else {
                      onUpdateM(match.id, { unit_price_override: +v });
                    }
                  }}
                  placeholder={defaultPrice.toFixed(2)}
                  className="w-12 h-6 rounded text-right text-[11px]"
                  data-testid={`action-price-mobile-${room.id}-${a.code}`}
                />
              </div>
            ) : null}
            <div
              className={`sm:hidden text-sm font-bold mt-0.5 ${checked ? "text-[#D96C4A]" : "text-[#A8A7A0]"}`}
              data-testid={`action-total-mobile-${room.id}-${a.code}`}
            >
              {fmtEUR(checked ? total : 0)}
            </div>
          </div>
        )}
        <div className="w-24 text-right hidden sm:block">
          <div className={`text-sm font-bold ${checked ? "text-[#D96C4A]" : "text-[#A8A7A0]"}`} data-testid={`action-total-${room.id}-${a.code}`}>
            {fmtEUR(checked ? total : 0)}
          </div>
        </div>
      </div>
    );
  };

  if (collapsed) {
    return (
      <div className="emma-card !p-0 overflow-hidden" data-testid={`room-quick-${room.id}`}>
        <button
          type="button"
          onClick={onToggleCollapsed}
          className="w-full px-4 sm:px-5 py-3 flex items-center gap-3 hover:bg-[#FAF9F6] text-left"
          data-testid={`room-collapsed-header-${room.id}`}
        >
          <ChevronRight className="w-4 h-4 text-[#5D7259] flex-shrink-0" />
          <Home className="w-4 h-4 text-[#D96C4A] flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-heading font-semibold text-[#2C2C2A] truncate" data-testid={`room-collapsed-name-${room.id}`}>
                {room.name}
              </span>
              <span
                className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${tplBadgeIsOverride ? "bg-[#D96C4A]/15 text-[#D96C4A]" : "bg-[#F2F5EE] text-[#3F5E3F]"}`}
                data-testid={`room-collapsed-template-${room.id}`}
              >
                {tplBadgeLabel}
              </span>
            </div>
            <div className="text-[11px] text-[#4F4F4D] mt-0.5 flex items-center gap-3 flex-wrap">
              <span data-testid={`room-collapsed-area-${room.id}`}>
                {isStair
                  ? `${+(room?.stair_props?.tread_count) || 0} treden`
                  : isGeneral
                    ? "Algemene werfkosten"
                    : `${wallArea.toFixed(2)} m² netto`}
              </span>
              <span>·</span>
              <span data-testid={`room-collapsed-lines-${room.id}`}>
                {totalLines} {totalLines === 1 ? "regel" : "regels"}
              </span>
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-[9px] uppercase tracking-wider text-[#5D7259]">Subtotaal</div>
            <div className="font-heading font-bold text-[#D96C4A]" data-testid={`room-collapsed-subtotal-${room.id}`}>
              {summary ? fmtEUR(summary.subtotal_excl) : "—"}
            </div>
          </div>
        </button>
      </div>
    );
  }

  return (
    <div className="emma-card !p-0 overflow-hidden" data-testid={`room-quick-${room.id}`}>
      <div className="px-4 sm:px-5 py-4 bg-gradient-to-r from-[#FBE9DF] to-transparent border-b border-[#E5E2DC] flex items-center gap-3">
        {onToggleCollapsed && (
          <button
            type="button"
            onClick={onToggleCollapsed}
            className="w-7 h-7 rounded-lg hover:bg-white/60 flex items-center justify-center flex-shrink-0"
            data-testid={`room-collapse-toggle-${room.id}`}
            aria-label="Kamer inklappen"
          >
            <ChevronDown className="w-4 h-4 text-[#4F4F4D]" />
          </button>
        )}
        <Home className="w-5 h-5 text-[#D96C4A] flex-shrink-0" />
        <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.15em] text-[#5D7259]">Kamer (soort)</Label>
            <Select value={room.room_type || "other"} onValueChange={(v) => onUpdateRoom({ room_type: v })}>
              <SelectTrigger className="h-10 mt-1 rounded-lg" data-testid={`room-type-select-${room.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROOM_TYPES.map((rt) => <SelectItem key={rt.key} value={rt.key}>{rt.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.15em] text-[#5D7259]">Naam</Label>
            <Input value={room.name} onChange={(e) => onUpdateRoom({ name: e.target.value })}
              onFocus={autoSelectOnFocus}
              className="h-10 mt-1 rounded-lg" data-testid={`room-name-${room.id}`} />
          </div>
        </div>
        {summary && (
          <div className="text-right hidden sm:block">
            <div className="text-[10px] text-[#4F4F4D]">Subtotaal</div>
            <div className="font-heading font-bold text-[#D96C4A]" data-testid={`room-subtotal-${room.id}`}>
              {fmtEUR(summary.subtotal_excl)}
            </div>
          </div>
        )}
        <button onClick={onRemove} className="w-10 h-10 rounded-lg hover:bg-red-50 flex items-center justify-center text-[#D9534F]" data-testid={`remove-room-${room.id}`}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      <div className="p-4 sm:p-5 space-y-4">
        {/* v6 F12 — Legacy stair-room banner: rooms met room_type="stair"
            blijven werken, maar we sturen de gebruiker naar "Werkzone → Trap"
            voor de nieuwe trap-flow met sjabloon-picker. */}
        {isStair && (
          <div className="rounded-xl border-2 border-[#7F4124]/40 bg-[#F7EDE4] px-4 py-3 flex items-start gap-2" data-testid={`stair-legacy-banner-${room.id}`}>
            <span className="text-[#7F4124] font-bold text-lg leading-none">⚠</span>
            <div className="text-[12px] text-[#7F4124] flex-1">
              <strong>Trap staat nog als &quot;ruimte&quot;.</strong> Voor de nieuwe trap-flow met sjabloon-picker en trap-specifieke werken:
              verwijder deze ruimte en voeg een <strong>Werkzone → Trap</strong> toe. Deze ruimte blijft werken tot je hem verwijdert.
            </div>
          </div>
        )}

        {/* v6 E8 / F11.7 — Per-room template override.
            Strikt 1:1 met DB: geen hardcoded "Project-standaard" optie meer.
            - Geen template gekozen      → placeholder "— Geen sjabloon —"
            - Template_id niet gevonden  → "Template niet gevonden" + waarschuwing */}
        {onChangeRoomTemplate && displayedTemplateOptions.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap" data-testid={`room-template-row-${room.id}`}>
            <Label className="text-[10px] uppercase tracking-[0.15em] text-[#5D7259] flex-shrink-0">Template</Label>
            <Select
              value={(roomTemplateMissing || roomTemplateLegacy) ? "" : (room.template_id || "")}
              onValueChange={(v) => {
                // v6 F16.2 — Legacy → nieuw: bevestiging vragen
                if (roomTemplateLegacy && v && v !== room.template_id) {
                  const ok = window.confirm(
                    "Oude sjabloonregels vervangen door nieuw sjabloon?\n\n" +
                    "De verouderde koppeling wordt vervangen. Extra werken die je zelf hebt toegevoegd blijven behouden."
                  );
                  if (!ok) return;
                }
                onChangeRoomTemplate(v || "");
              }}
            >
              <SelectTrigger
                className={`h-9 rounded-lg flex-1 max-w-md text-xs ${roomTemplateMissing ? "border-[#D9534F] text-[#D9534F]" : roomTemplateLegacy ? "border-[#E4B16D] text-[#7F4124] bg-[#FFF8ED]" : ""}`}
                data-testid={`room-template-select-${room.id}`}
              >
                <SelectValue placeholder={
                  roomTemplateLegacy ? "⚠ Verouderd sjabloon — kies een nieuw actief sjabloon"
                  : roomTemplateMissing ? "Template niet gevonden — kies een nieuwe"
                  : "— Geen sjabloon —"
                } />
              </SelectTrigger>
              <SelectContent>
                {displayedTemplateOptions.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}{t.__legacyPassthrough ? "  (legacy)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {room.template_id && !roomTemplateMissing && !roomTemplateLegacy && (
              <button
                type="button"
                onClick={() => onChangeRoomTemplate("")}
                className="h-9 px-2 rounded-lg border border-[#E5E2DC] text-[10px] text-[#4F4F4D] hover:bg-[#F0EFEA] hover:border-[#D96C4A]"
                data-testid={`room-template-clear-${room.id}`}
                title="Sjabloon wissen"
              >
                ✕ Wis
              </button>
            )}
            {roomTemplateLegacy && (
              <span
                className="text-[10px] text-[#7F4124] font-bold uppercase tracking-wider inline-flex items-center gap-1"
                data-testid={`room-template-legacy-${room.id}`}
                title={`Sjabloon ${activeRoomTpl?.name} is gedeactiveerd`}
              >
                ⚠ Verouderd
              </span>
            )}
            {roomTemplateMissing && (
              <span
                className="text-[10px] text-[#D9534F] font-bold uppercase tracking-wider inline-flex items-center gap-1"
                data-testid={`room-template-missing-${room.id}`}
                title={`template_id ${room.template_id} bestaat niet meer in de bibliotheek`}
              >
                ⚠ Niet gevonden
              </span>
            )}
            {room.template_id && !roomTemplateMissing && (
              <span className="text-[10px] text-[#D96C4A] font-bold uppercase tracking-wider" data-testid={`room-template-override-badge-${room.id}`}>
                Override
              </span>
            )}
          </div>
        )}

        {/* v6 F9.2 — Trap-zone: stair-specifieke invoer i.p.v. L/B/H + wand.
            v6 F9.3 Batch C — Algemeen-zone: alléén werken-lijst, geen extra
            invoerblok (kamernaam is de enige identifier). */}
        {isStair ? (
          <StairPropsBlock room={room} onUpdateRoom={onUpdateRoom} />
        ) : isGeneral ? (
          <div
            className="border border-[#E5E2DC] rounded-xl bg-[#FAF9F6] p-3 text-[11px] text-[#4F4F4D] italic"
            data-testid={`general-zone-hint-${room.id}`}
          >
            Algemene werfkosten — niet aan een ruimte gekoppeld. Pas een template toe
            (bv. <strong>Algemene werfkosten</strong>) of voeg A-codes toe via
            <strong> "Andere code toevoegen"</strong>. Geef je zone een herkenbare naam
            zoals "Werfkosten", "Meerwerken" of "Hoogtewerker" — die verschijnt in de
            offerte/PDF als <strong>ALGEMEEN — &lt;naam&gt;</strong>.
          </div>
        ) : (
        <>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          <NumF label="Lengte (L)" value={L} onChange={(v) => onUpdateRoom({ length: v })} testId={`room-l-${room.id}`} placeholder="0,00" />
          <NumF label="Breedte (B)" value={W} onChange={(v) => onUpdateRoom({ width: v })} testId={`room-b-${room.id}`} placeholder="0,00" />
          <NumF label="Hoogte (H)" value={H} onChange={(v) => onUpdateRoom({ height: v })} testId={`room-h-${room.id}`} placeholder="0,00" />
          <div className="bg-[#FBE9DF] border border-[#F4D6C4] rounded-xl px-3 py-2 flex flex-col justify-center">
            <div className="text-[10px] uppercase tracking-[0.1em] text-[#5D7259] font-bold">Wand netto</div>
            <div className="font-heading font-bold text-base sm:text-lg text-[#2C2C2A]" data-testid={`room-area-${room.id}`}>
              {wallArea.toFixed(2)} m²
            </div>
            <div className="text-[10px] text-[#4F4F4D]">
              {wallDedu > 0 ? <>bruto {wallGross.toFixed(2)} − {wallDedu.toFixed(2)}</> : <>wand · {ceilingArea.toFixed(2)} m² plafond</>}
            </div>
          </div>
        </div>

        {/* v6 F7 — Meetmethode-toggle (snelle vs vrije opmeting) */}
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-[0.1em] font-bold text-[#5D7259]">
            Meetmethode
          </span>
          <div className="inline-flex rounded-xl border border-[#E5E2DC] overflow-hidden bg-white"
               data-testid={`measure-method-${room.id}`}>
            <button
              type="button"
              onClick={() => {
                if (room.measure_method === "free") {
                  // Free → quick: behoud segments in geheugen, switch alleen flag.
                  onUpdateRoom({ measure_method: "quick" });
                }
              }}
              className={`px-3 h-8 text-xs font-medium transition ${
                !isFree ? "bg-[#2C2C2A] text-white" : "text-[#4F4F4D] hover:bg-[#F0EFEA]"
              }`}
              data-testid={`measure-method-quick-${room.id}`}
            >
              Snelle opmeting
            </button>
            <button
              type="button"
              onClick={() => {
                if (room.measure_method !== "free") {
                  // Quick → free: pre-fill segments uit L×W×H als nog niet aanwezig.
                  const hasWalls = (room.wall_segments || []).length > 0;
                  const hasCeil = (room.ceiling_segments || []).length > 0;
                  const patch = { measure_method: "free" };
                  if (!hasWalls || !hasCeil) {
                    const built = buildDefaultSegmentsFromQuick(room);
                    if (!hasWalls) patch.wall_segments = built.wall_segments;
                    if (!hasCeil) patch.ceiling_segments = built.ceiling_segments;
                  }
                  onUpdateRoom(patch);
                }
              }}
              className={`px-3 h-8 text-xs font-medium transition ${
                isFree ? "bg-[#2C2C2A] text-white" : "text-[#4F4F4D] hover:bg-[#F0EFEA]"
              }`}
              data-testid={`measure-method-free-${room.id}`}
            >
              Vrije opmeting
            </button>
          </div>
          <span className="text-[11px] text-[#4F4F4D]">
            {isFree
              ? "Voeg afzonderlijke muur- en plafondvlakken toe."
              : "Standaard rechthoekige kamer met L × B × H."}
          </span>
        </div>

        {/* v6 F7 — Vrije opmeting: muren + plafonds segmenten editor */}
        {isFree && (
          <FreeMeasureEditor room={room} onUpdateRoom={onUpdateRoom} />
        )}

        {/* v6 E2 — Wall deductions block (door/window/cabinet/etc.) */}
        <RoomDeductionsBlock room={room} onUpdateRoom={onUpdateRoom} />
        </>
        )}

        <div className="border-t border-[#E5E2DC] pt-3">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-[#2C2C2A]">Selecteer de werken</h3>
            <button onClick={onSwitchAdvanced} className="text-[11px] text-[#D96C4A] hover:underline" data-testid={`switch-advanced-${room.id}`}>
              Detail-modus
            </button>
          </div>
          <TooltipProvider delayDuration={150}>
            {(() => {
              const sections = groupRoomActions({ defaults, customMs, codeByCode });
              return sections.map((sec) => {
                // v6 F1.1 — Subtotaal per sectie: som van checked default-rows + custom-rows.
                // Defaults: alleen meetellen als ze een match hebben (checked).
                let secTotal = 0;
                sec.actions.forEach((a) => {
                  const match = actionMatch(a);
                  if (!match) return;
                  const pc = codeByCode[a.code];
                  // v6 F42k v14e final — precedence spiegelt regel-weergave (r146+):
                  // unit_price_override → unit_price_snapshot → pc.default_price → 0.
                  // Voorheen: alleen pc.default_price → sectie-subtotaal wijkt af van
                  // som van zichtbare regeltotalen bij prijs-override.
                  const unitPrice =
                    (match.unit_price_override !== undefined && match.unit_price_override !== null)
                      ? +match.unit_price_override
                      : (match.unit_price_snapshot != null
                          ? +match.unit_price_snapshot
                          : (pc ? +pc.default_price : 0));
                  const applyDed = !!pc?.apply_wall_deductions;
                  let qty;
                  if (a.auto_kind === "ceiling" || a.auto_kind === "floor") qty = (+match.length || 0) * (+match.width || 0);
                  else if (a.auto_kind === "plinten" || a.auto_kind === "linear") qty = +match.length || 0;
                  else if (a.auto_kind === "fixed") qty = +match.quantity || 1;
                  else qty = +match.quantity || 0;
                  if (!match.quantity && a.auto_kind !== "ceiling" && a.auto_kind !== "floor" && a.auto_kind !== "plinten" && a.auto_kind !== "linear") {
                    qty = autoQuantityForAction(a, room, applyDed);
                  }
                  secTotal += qty * unitPrice;
                });
                sec.customMs.forEach((m) => {
                  const pc = m.price_code_id ? priceCodes.find((p) => p.id === m.price_code_id) : null;
                  const unitPrice = m.unit_price_override ?? pc?.default_price ?? 0;
                  const L = +m.length || 0, H = +m.height || 0, W = +m.width || 0, Q = +m.quantity || 0;
                  const mult = Math.max(1, +m.multiplier || 1);
                  let gross = 0;
                  if (m.formula === "wall") gross = L * H;
                  else if (m.formula === "ceiling" || m.formula === "floor") gross = L * W;
                  else if (m.formula === "linear") gross = L;
                  else if (m.formula === "piece" || m.formula === "hour" || m.formula === "custom") gross = Q;
                  else if (m.formula === "fixed") gross = 1;
                  const dedu = (m.deductions || []).reduce((s, d) => ((+d.area || 0) >= 1 ? s + (+d.area || 0) : s), 0);
                  const netUnit = Math.max(0, m.formula === "wall" ? gross - dedu : gross);
                  secTotal += netUnit * mult * (+unitPrice || 0);
                });
                return (
                <div
                  key={sec.key}
                  className="mt-2 first:mt-0"
                  data-testid={`room-section-${room.id}-${sec.key}`}
                >
                  <div className="flex items-baseline justify-between gap-2 sticky top-0 bg-white/95 backdrop-blur-sm py-1 z-[1] mb-1.5">
                    <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#5D7259]">
                      {sec.label}
                    </div>
                    {secTotal > 0 && (
                      <div
                        className="text-[11px] font-semibold text-[#4F4F4D]"
                        data-testid={`room-section-subtotal-${room.id}-${sec.key}`}
                      >
                        {fmtEUR(secTotal)}
                      </div>
                    )}
                  </div>
                  <div className="border border-[#E5E2DC] rounded-xl overflow-hidden divide-y divide-[#F0EFEA]">
                    {(() => {
                      // v6 F42k Correctieronde v8 — Choice-group header render.
                      // Wanneer een reeks actions dezelfde choice_group deelt,
                      // toon een header "SCHUURGRAAD — KIES ÉÉN" boven de eerste.
                      let lastCg = null;
                      const out = [];
                      for (const a of sec.actions) {
                        const cg = a.choice_group;
                        if (cg && cg !== lastCg) {
                          out.push(
                            <div
                              key={`cg-header-${cg}`}
                              className="px-3 py-1.5 bg-[#FFF8ED] border-b border-[#F2DCB2] text-[9px] uppercase tracking-[0.18em] font-bold text-[#7F4124]"
                              data-testid={`choice-group-header-${room.id}-${sec.key}-${cg}`}
                            >
                              {CG_HEADERS[cg] || `${cg.replace(/_/g, " ").toUpperCase()} — KIES ÉÉN`}
                            </div>
                          );
                        }
                        lastCg = cg || null;
                        out.push(renderActionRow(a));
                      }
                      return out;
                    })()}
                    {sec.customMs.length > 0 && sec.customMs.map((m, idx) => (
                      <div key={m.id} className="px-3 py-2" data-testid={`custom-row-${room.id}-${m.id}`}>
                        <MeasurementRow m={m} idx={idx + 1} priceCodes={priceCodes}
                          onChange={(p) => onUpdateM(m.id, p)}
                          onRemove={() => onRemoveM(m.id)}
                          onDuplicate={() => {}}
                          onAddDeduction={() => {}}
                          onUpdDeduction={() => {}}
                          onRemDeduction={() => {}}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                );
              });
            })()}
          </TooltipProvider>
        </div>

        <div className="pt-2 border-t border-[#E5E2DC] flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => onAddCustomMeasurement()} className="h-9 rounded-xl text-xs gap-1 border-dashed" data-testid={`add-other-${room.id}`}>
            <Plus className="w-3 h-3" /> Andere code toevoegen
          </Button>
        </div>

        {summary && (
          <div className="sm:hidden border-t border-[#E5E2DC] pt-2 flex justify-between items-center">
            <span className="text-xs uppercase font-bold text-[#5D7259]">Subtotaal</span>
            <span className="font-heading font-bold text-[#D96C4A]">{fmtEUR(summary.subtotal_excl)}</span>
          </div>
        )}
      </div>
    </div>
  );
}


// v6 F7 — Vrije opmeting editor. Compacte 1-regel input per segment,
// optimaal voor mobiel. Muren eerst, dan plafonds. Verwijder-knop per regel.
function FreeMeasureEditor({ room, onUpdateRoom }) {
  const walls = room.wall_segments || [];
  const ceils = room.ceiling_segments || [];

  const newId = () => (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `seg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);

  const wallsTotal = walls.reduce(
    (s, w) => s + (Math.max(0, +w.length || 0) * Math.max(0, +w.height || 0)),
    0,
  );
  const ceilsTotal = ceils.reduce(
    (s, c) => s + (Math.max(0, +c.length || 0) * Math.max(0, +c.width || 0)),
    0,
  );

  const updateWall = (id, patch) => onUpdateRoom({
    wall_segments: walls.map((w) => (w.id === id ? { ...w, ...patch } : w)),
  });
  const removeWall = (id) => onUpdateRoom({
    wall_segments: walls.filter((w) => w.id !== id),
  });
  const addWall = () => onUpdateRoom({
    wall_segments: [
      ...walls,
      { id: newId(), label: `Muur ${walls.length + 1}`, length: 0, height: +room.height || 0 },
    ],
  });

  const updateCeil = (id, patch) => onUpdateRoom({
    ceiling_segments: ceils.map((c) => (c.id === id ? { ...c, ...patch } : c)),
  });
  const removeCeil = (id) => onUpdateRoom({
    ceiling_segments: ceils.filter((c) => c.id !== id),
  });
  const addCeil = () => onUpdateRoom({
    ceiling_segments: [
      ...ceils,
      { id: newId(), label: `Plafond ${ceils.length + 1}`, length: 0, width: 0 },
    ],
  });

  return (
    <div
      className="mt-3 border border-[#E5E2DC] rounded-xl bg-[#FAF9F6] p-3 space-y-4"
      data-testid={`free-measure-editor-${room.id}`}
    >
      <SegmentSection
        title="Muren"
        subtitle="Lengte × hoogte per muurvlak"
        items={walls}
        renderRow={(w) => (
          <SegmentRow
            key={w.id}
            seg={w}
            placeholders={{ a: "Lengte", b: "Hoogte" }}
            valueA={w.length}
            valueB={w.height}
            onLabel={(v) => updateWall(w.id, { label: v })}
            onA={(v) => updateWall(w.id, { length: v })}
            onB={(v) => updateWall(w.id, { height: v })}
            onRemove={() => removeWall(w.id)}
            testIdPrefix={`wall-${w.id}`}
          />
        )}
        onAdd={addWall}
        addLabel="Muur toevoegen"
        addTestId={`add-wall-${room.id}`}
        totalLabel="Totaal bruto muren"
        totalValue={wallsTotal}
        totalTestId={`walls-total-${room.id}`}
      />

      <SegmentSection
        title="Plafonds"
        subtitle="Lengte × breedte per plafondvlak"
        items={ceils}
        renderRow={(c) => (
          <SegmentRow
            key={c.id}
            seg={c}
            placeholders={{ a: "Lengte", b: "Breedte" }}
            valueA={c.length}
            valueB={c.width}
            onLabel={(v) => updateCeil(c.id, { label: v })}
            onA={(v) => updateCeil(c.id, { length: v })}
            onB={(v) => updateCeil(c.id, { width: v })}
            onRemove={() => removeCeil(c.id)}
            testIdPrefix={`ceil-${c.id}`}
          />
        )}
        onAdd={addCeil}
        addLabel="Plafond toevoegen"
        addTestId={`add-ceiling-${room.id}`}
        totalLabel="Totaal plafond"
        totalValue={ceilsTotal}
        totalTestId={`ceiling-total-${room.id}`}
      />
    </div>
  );
}

function SegmentSection({ title, subtitle, items, renderRow, onAdd, addLabel, addTestId, totalLabel, totalValue, totalTestId }) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 mb-2 flex-wrap">
        <div className="flex items-baseline gap-2">
          <Ruler className="w-3.5 h-3.5 text-[#5D7259] self-center" />
          <span className="text-sm font-bold text-[#2C2C2A]">{title}</span>
          <span className="text-[10px] text-[#5D7259]">— {subtitle}</span>
        </div>
        <Button
          variant="outline"
          onClick={onAdd}
          className="h-7 px-2 rounded-lg gap-1 text-[11px] border-[#E5E2DC]"
          data-testid={addTestId}
        >
          <Plus className="w-3 h-3" /> {addLabel}
        </Button>
      </div>
      {items.length === 0 && (
        <div className="text-[11px] italic text-[#5D7259] px-2 py-3 border border-dashed border-[#E5E2DC] rounded-lg text-center">
          Nog geen vlak. Tik op <strong>{addLabel}</strong>.
        </div>
      )}
      <div className="space-y-1.5">{items.map(renderRow)}</div>
      <div className="mt-2 flex items-center justify-between px-2 py-1.5 rounded-lg bg-[#FBE9DF] border border-[#F4D6C4]">
        <span className="text-[11px] uppercase tracking-[0.1em] font-bold text-[#7F4124]">
          {totalLabel}
        </span>
        <span
          className="font-heading font-bold text-sm text-[#2C2C2A]"
          data-testid={totalTestId}
        >
          {totalValue.toFixed(2)} m²
        </span>
      </div>
    </div>
  );
}

// Compacte 1-regel rij: [label] [A] × [B] = m² · trash
function SegmentRow({ seg, placeholders, valueA, valueB, onLabel, onA, onB, onRemove, testIdPrefix }) {
  const area = Math.max(0, +valueA || 0) * Math.max(0, +valueB || 0);
  return (
    <div
      className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-white border border-[#E5E2DC]"
      data-testid={testIdPrefix}
    >
      <Input
        value={seg.label || ""}
        onChange={(e) => onLabel(e.target.value)}
        placeholder="Label"
        className="h-8 rounded-md text-xs w-20 sm:w-28 flex-shrink-0"
        data-testid={`${testIdPrefix}-label`}
      />
      <NumF
        value={valueA}
        onChange={onA}
        placeholder={placeholders.a}
        testId={`${testIdPrefix}-a`}
        compact
      />
      <span className="text-[#5D7259] text-xs">×</span>
      <NumF
        value={valueB}
        onChange={onB}
        placeholder={placeholders.b}
        testId={`${testIdPrefix}-b`}
        compact
      />
      <span className="text-[#5D7259] text-xs">=</span>
      <span
        className="text-xs font-mono font-bold text-[#2C2C2A] w-16 sm:w-20 text-right flex-shrink-0"
        data-testid={`${testIdPrefix}-area`}
      >
        {area.toFixed(2)} m²
      </span>
      <button
        type="button"
        onClick={onRemove}
        className="w-8 h-8 rounded-md hover:bg-red-50 flex items-center justify-center text-[#D9534F] flex-shrink-0"
        aria-label="Verwijder vlak"
        data-testid={`${testIdPrefix}-remove`}
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
